// Simple bilingual translations (FR/EN) with LocalStorage persistence.
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const dict = {
  fr: {
    nav: {
      home: "Accueil",
      experiences: "Expériences",
      gallery: "Galerie",
      journal: "Journal",
      about: "Notre histoire",
      contact: "Contact",
      book: "Prendre rendez-vous",
      portal: "Espace client",
      crm: "CRM",
      login: "Connexion",
      logout: "Déconnexion",
    },
    home: {
      overline: "Voyages sur-mesure — France",
      title_1: "L'art du voyage,",
      title_2: "à la française.",
      lede: "Mariages en château, escapades en Champagne, circuits privés. Une équipe française, des adresses gardées, un service qui n'appartient qu'à vous.",
      cta_book: "Appel découverte gratuit",
      cta_services: "Explorer les expériences",
      section_signature: "Signatures",
      sig_lede: "Six manières de découvrir la France, en toute discrétion.",
      sig_wedding: "Mariages en château",
      sig_wedding_d: "Formules Essential, Signature, Prestige — de la Loire au Périgord.",
      sig_champagne: "Champagne Escape",
      sig_champagne_d: "Trois jours entre grandes maisons et vignerons confidentiels.",
      sig_private: "Circuits privés",
      sig_private_d: "Loire, Bourgogne, Provence, Alsace — un itinéraire à votre rythme.",
      sig_paris: "Paris & Campagne",
      sig_paris_d: "La ville-lumière prolongée d'une échappée à moins d'une heure.",
      sig_honeymoon: "Voyages de noces",
      sig_honeymoon_d: "Le luxe discret d'un séjour pensé pour deux.",
      sig_proposal: "Demandes en mariage",
      sig_proposal_d: "Une mise en scène orchestrée, jusqu'au moindre détail.",
      section_process: "Le parcours",
      process_lede: "Dix étapes pour transformer une envie en un souvenir.",
      section_testimonials: "Ils nous ont fait confiance",
      cta_newsletter: "Recevoir nos itinéraires confidentiels",
      newsletter_placeholder: "Votre email",
      newsletter_submit: "S'inscrire",
    },
    booking: {
      title: "Un appel, une conversation.",
      lede: "Trente minutes en visio avec Lucile — sans engagement, en français ou en anglais.",
      form_name: "Nom complet",
      form_email: "Email",
      form_phone: "Téléphone",
      form_party: "Nombre de personnes",
      form_type: "Type d'expérience",
      form_notes: "Parlez-nous de votre projet",
      pick_slot: "Choisissez un créneau",
      submit: "Confirmer le rendez-vous",
      types: {
        discovery: "Appel découverte",
        wedding: "Mariage en château",
        champagne: "Champagne Escape",
        private: "Circuit privé",
        honeymoon: "Voyage de noces",
        proposal: "Demande en mariage",
        corporate: "Séminaire d'entreprise",
      },
      success: "Rendez-vous confirmé. Un email récapitulatif vous a été envoyé.",
    },
    contact: {
      title: "Écrivez-nous.",
      lede: "Nous répondons à chaque message personnellement, sous 24 heures ouvrées.",
      submit: "Envoyer",
      success: "Message envoyé. À très vite.",
    },
    about: {
      title: "Trois voix, une maison.",
      lede: "French Heritage Experiences est né d'une conviction : le luxe véritable est celui du temps, du soin et des rencontres.",
      lucile: "Lucile — Direction & Commercial",
      lucile_d: "Vingt ans à orchestrer les voyages les plus exigeants. Elle sera votre unique interlocutrice.",
      pascal: "Pascal — Partenariats France",
      pascal_d: "Il connaît chaque château, chaque maison de champagne, chaque photographe qui compte.",
      camille: "Camille — Marketing & Récits",
      camille_d: "Elle raconte nos voyages avec la précision d'un magazine et l'âme d'un carnet intime.",
    },
    footer: {
      tag: "Voyages français sur-mesure.",
      rights: "Tous droits réservés.",
      links: "Liens",
      legal: "Mentions légales",
      privacy: "Confidentialité",
    },
  },
  en: {
    nav: {
      home: "Home",
      experiences: "Experiences",
      gallery: "Gallery",
      journal: "Journal",
      about: "Our story",
      contact: "Contact",
      book: "Book a call",
      portal: "Client portal",
      crm: "CRM",
      login: "Sign in",
      logout: "Sign out",
    },
    home: {
      overline: "Tailor-made travel — France",
      title_1: "The art of travel,",
      title_2: "the French way.",
      lede: "Château weddings, Champagne escapes, private tours. A French team, guarded addresses, service that belongs only to you.",
      cta_book: "Free discovery call",
      cta_services: "Explore experiences",
      section_signature: "Signatures",
      sig_lede: "Six ways to discover France, in perfect discretion.",
      sig_wedding: "Château weddings",
      sig_wedding_d: "Essential, Signature, Prestige — from the Loire to Périgord.",
      sig_champagne: "Champagne Escape",
      sig_champagne_d: "Three days between grand houses and confidential growers.",
      sig_private: "Private tours",
      sig_private_d: "Loire, Burgundy, Provence, Alsace — at your own pace.",
      sig_paris: "Paris & Countryside",
      sig_paris_d: "The city of light extended by an escape within the hour.",
      sig_honeymoon: "Honeymoons",
      sig_honeymoon_d: "Quiet luxury, designed for two.",
      sig_proposal: "Proposals",
      sig_proposal_d: "An orchestrated moment, down to the smallest detail.",
      section_process: "The journey",
      process_lede: "Ten steps to turn a whim into a memory.",
      section_testimonials: "In their words",
      cta_newsletter: "Receive our confidential itineraries",
      newsletter_placeholder: "Your email",
      newsletter_submit: "Subscribe",
    },
    booking: {
      title: "One call, one conversation.",
      lede: "Thirty minutes on video with Lucile — no obligation, in French or English.",
      form_name: "Full name",
      form_email: "Email",
      form_phone: "Phone",
      form_party: "Party size",
      form_type: "Experience",
      form_notes: "Tell us about your project",
      pick_slot: "Pick a slot",
      submit: "Confirm booking",
      types: {
        discovery: "Discovery call",
        wedding: "Château wedding",
        champagne: "Champagne Escape",
        private: "Private tour",
        honeymoon: "Honeymoon",
        proposal: "Proposal",
        corporate: "Corporate retreat",
      },
      success: "Booking confirmed. A summary email is on its way.",
    },
    contact: {
      title: "Write to us.",
      lede: "Every message is answered personally, within 24 business hours.",
      submit: "Send",
      success: "Message sent. Talk soon.",
    },
    about: {
      title: "Three voices, one house.",
      lede: "French Heritage Experiences was born of a conviction: true luxury is time, care, and encounters.",
      lucile: "Lucile — Direction & Sales",
      lucile_d: "Twenty years orchestrating the most demanding travel. She'll be your only point of contact.",
      pascal: "Pascal — French partnerships",
      pascal_d: "He knows every château, every champagne house, every photographer that matters.",
      camille: "Camille — Marketing & Storytelling",
      camille_d: "She tells our journeys with a magazine's precision and a private diary's soul.",
    },
    footer: {
      tag: "French tailor-made travel.",
      rights: "All rights reserved.",
      links: "Links",
      legal: "Legal",
      privacy: "Privacy",
    },
  },
};

const I18nContext = createContext({ lang: "fr", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "fr");
  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(() => {
    const t = (path) => {
      const parts = path.split(".");
      let cur = dict[lang];
      for (const p of parts) {
        if (cur == null) return path;
        cur = cur[p];
      }
      return cur ?? path;
    };
    return { lang, setLang: setLangState, t };
  }, [lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
